import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { Competition } from '../types/interfaces';
import Button from '../components/common/Button';
import CompetitionTypePill from '../components/common/CompetitionTypePill';
import CompetitionVisibilityPill from '../components/common/CompetitionVisibilityPill';
import CompetitionPrizePill from '../components/common/CompetitionPrizePill';
import LoadingSpinner from '../components/common/LoadingSpinner';
import SiteFooter from '../components/common/SiteFooter';
import BrandLogo from '../components/common/BrandLogo';
import BrandBackdrop from '../components/common/BrandBackdrop';
import CompetitionArtwork from '../components/common/CompetitionArtwork';
import { formatDate } from '../utils/dates';
import './Home.css';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const Home: React.FC = () => {
  useDocumentTitle();
  const { isAuthenticated } = useAuth();
  const [showcaseCompetitions, setShowcaseCompetitions] = useState<Competition[]>([]);
  const [showcaseLoading, setShowcaseLoading] = useState(true);
  const [showcaseError, setShowcaseError] = useState('');

  useEffect(() => {
    const fetchShowcase = async () => {
      try {
        setShowcaseLoading(true);
        setShowcaseError('');
        const response = await apiService.get('/competitions/public/list', { params: { limit: 6 } });
        setShowcaseCompetitions(Array.isArray(response.data) ? response.data : []);
      } catch (err: any) {
        setShowcaseError(err?.message || 'Unable to load public showcase competitions');
      } finally {
        setShowcaseLoading(false);
      }
    };

    fetchShowcase();
  }, []);

  return (
    <div className="home-page">
      <section className="home-hero">
        <BrandBackdrop />
        <div className="container">
          <div className="home-hero__content">
            <div className="home-hero__text">
              <BrandLogo reversed className="home-hero__logo" />
              <h1 className="home-hero__title home-hero__title--slogan">
                <span className="home-hero__word home-hero__word--blue">Create.</span>{' '}
                <span className="home-hero__word home-hero__word--orange">Compete.</span>{' '}
                <span className="home-hero__word home-hero__word--blue">Advance.</span>
              </h1>
              <p className="home-hero__subtitle">
                Join iKompete to explore competitions, submit your work, and grow through peer evaluation.
              </p>

              <div className="home-hero__actions">
                {isAuthenticated ? (
                  <Button as={Link} to="/dashboard" variant="secondary" size="lg">
                    Dashboard
                  </Button>
                ) : (
                  <Button as={Link} to="/login" variant="secondary" size="lg">
                    Get Started
                  </Button>
                )}
              </div>
            </div>
            <CompetitionArtwork />
          </div>
        </div>
      </section>

      <section id="platform-video" className="home-video">
        <div className="container">
          <div className="home-video__header">
            <h2>See iKompete in action</h2>
            <p>A quick walkthrough of the platform and recent showcase competitions.</p>
          </div>

          <div className="home-video__content" aria-label="iKompete promo video">
            <video
              className="home-video__player"
              autoPlay
              muted
              loop
              controls
              preload="metadata"
              playsInline
            >
              <source
                src="https://s3.dsrs.illinois.edu/ikompete/competitions/public/assets/iKompetePromo.mp4"
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          </div>

          <div className="home-showcase">
            <div className="home-showcase__header">
              <h3>Public Showcase Competitions</h3>
            </div>

            {showcaseLoading ? (
              <div className="home-showcase__loading">
                <LoadingSpinner size="sm" text="Loading showcases..." />
              </div>
            ) : showcaseError ? (
              <p className="home-showcase__empty">{showcaseError}</p>
            ) : showcaseCompetitions.length === 0 ? (
              <p className="home-showcase__empty">No showcase competitions are published yet.</p>
            ) : (
              <div className="home-showcase__grid">
                {showcaseCompetitions.map((competition) => {
                  const isTeamMode = (competition.competition_mode || 'individual') === 'team';
                  const participantLabel = competition.participants === 1
                    ? (isTeamMode ? 'team' : 'participant')
                    : (isTeamMode ? 'teams' : 'participants');

                  return (
                    <article key={competition.id} className="home-showcase__card">
                      <div className="home-showcase__card-head">
                        {competition.logo_path ? (
                          <img
                            src={competition.logo_path}
                            alt={`${competition.title} logo`}
                            className="home-showcase__logo"
                          />
                        ) : null}
                        <h4>{competition.title}</h4>
                      </div>

                      <p className="home-showcase__description">
                        {(competition.description || '').length > 200
                          ? `${(competition.description || '').slice(0, 200)}...`
                          : (competition.description || 'No description provided.')}
                      </p>

                      <div className="home-showcase__meta">
                        <span>{formatDate(competition.end_date)}</span>
                        {competition.participants !== undefined ? (
                          <span>{competition.participants} {participantLabel}</span>
                        ) : null}
                      </div>

                      <div className="home-showcase__pills">
                        <CompetitionTypePill type={competition.type} />
                        <CompetitionVisibilityPill visibility={competition.visibility} />
                        <CompetitionPrizePill prize={competition.prize} />
                      </div>

                      <Button as={Link} to={`/public/competition/${competition.id}`} variant="outline" size="sm">
                        Open
                      </Button>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default Home;
